import type {
  AlertRule,
  FleetFilter,
  FleetNode,
  MaintenanceWindow,
  NodeStatus,
  Project,
  ProjectMapPolicy,
  ServiceObservation,
} from "./domain";
import { classifyNode } from "./domain";

/** Derived project health and availability evidence. */
export interface ProjectHealth {
  readonly total: number;
  readonly healthy: number;
  readonly attention: number;
  readonly worst: NodeStatus | "empty";
  readonly availabilityPercent: number | null;
  readonly sampleCount: number;
}

/** One retained node contact used for time-weighted availability. */
export interface AvailabilityContact {
  readonly nodeId: string;
  readonly observedAt: string;
  readonly health: "healthy" | "warning" | "critical";
}

/** One planned interval excluded from an assigned node's availability denominator. */
export interface AvailabilityMaintenance {
  readonly nodeId: string;
  readonly startsAt: string;
  readonly endsAt: string;
}

/** Derive node-time availability while counting missing expected contacts as unavailable. */
export function deriveContactAvailability(
  nodeIds: readonly string[],
  contacts: readonly AvailabilityContact[],
  startsAt: Date,
  endsAt: Date,
  cadenceSeconds = 300,
  graceSeconds = 900,
  maintenance: readonly AvailabilityMaintenance[] = [],
): {
  readonly samples: number;
  readonly available: number;
  readonly excludedMaintenanceSamples: number;
  readonly availabilityPercent: number | null;
} {
  if (
    nodeIds.length === 0 || endsAt <= startsAt || cadenceSeconds < 60 ||
    graceSeconds < cadenceSeconds
  ) {
    return {
      samples: 0,
      available: 0,
      excludedMaintenanceSamples: 0,
      availabilityPercent: null,
    };
  }
  const byNode = new Map(
    nodeIds.map((nodeId) => [
      nodeId,
      contacts.filter((contact) => contact.nodeId === nodeId).sort((
        left,
        right,
      ) => Date.parse(left.observedAt) - Date.parse(right.observedAt)),
    ]),
  );
  const positions = new Map(nodeIds.map((nodeId) => [nodeId, -1]));
  let samples = 0;
  let available = 0;
  let excludedMaintenanceSamples = 0;
  const cadence = cadenceSeconds * 1_000;
  const grace = graceSeconds * 1_000;
  for (
    let cursor = startsAt.getTime();
    cursor < endsAt.getTime();
    cursor += cadence
  ) {
    for (const nodeId of nodeIds) {
      const planned = maintenance.some((window) =>
        window.nodeId === nodeId && Date.parse(window.startsAt) <= cursor &&
        Date.parse(window.endsAt) > cursor
      );
      if (planned) {
        excludedMaintenanceSamples += 1;
        continue;
      }
      samples += 1;
      const nodeContacts = byNode.get(nodeId) ?? [];
      let position = positions.get(nodeId) ?? -1;
      while (
        position + 1 < nodeContacts.length &&
        Date.parse(nodeContacts[position + 1]?.observedAt ?? "") <= cursor
      ) position += 1;
      positions.set(nodeId, position);
      const latest = position >= 0 ? nodeContacts[position] : undefined;
      if (
        latest && cursor - Date.parse(latest.observedAt) <= grace &&
        (latest.health === "healthy" || latest.health === "warning")
      ) available += 1;
    }
  }
  return {
    samples,
    available,
    excludedMaintenanceSamples,
    availabilityPercent: samples === 0
      ? null
      : Math.round((available / samples) * 1_000) / 10,
  };
}

const severityRank: Readonly<Record<NodeStatus, number>> = {
  healthy: 0,
  warning: 1,
  stale: 2,
  critical: 3,
  offline: 4,
};

/** Derive project status and availability from assigned nodes and retained samples. */
export function deriveProjectHealth(
  nodes: readonly FleetNode[],
  now: Date,
  availabilitySamples: readonly boolean[],
): ProjectHealth {
  const statuses = nodes.map((item) => classifyNode(item, now));
  const worst = statuses.length === 0
    ? "empty"
    : statuses.reduce((current, status) =>
      severityRank[status] > severityRank[current] ? status : current
    );
  const healthy = statuses.filter((status) => status === "healthy").length;
  const available = availabilitySamples.filter(Boolean).length;
  return {
    total: nodes.length,
    healthy,
    attention: nodes.length - healthy,
    worst,
    availabilityPercent: availabilitySamples.length === 0
      ? null
      : Math.round((available / availabilitySamples.length) * 1_000) / 10,
    sampleCount: availabilitySamples.length,
  };
}

/** Filter nodes using only the closed search, status, and tag grammar. */
export function filterFleet(
  nodes: readonly FleetNode[],
  filter: FleetFilter,
  now: Date,
): readonly FleetNode[] {
  const query = filter.query.trim().toLocaleLowerCase();
  return nodes.filter((item) => {
    const status = classifyNode(item, now);
    const tags = item.tags ?? [];
    const projectIds = item.projectIds ?? [];
    const projectNames = item.projectNames ?? [];
    const locations = item.locations ?? [];
    const capabilities = item.capabilities ?? [];
    const ageMinutes = Math.max(
      0,
      (now.getTime() - Date.parse(item.lastSeenAt)) / 60_000,
    );
    const matchesQuery = !query ||
      [
        item.name,
        item.architecture,
        item.release,
        ...tags,
        ...projectNames,
        ...locations,
        ...capabilities,
      ].some((value) => value.toLocaleLowerCase().includes(query));
    const matchesStatus = filter.statuses.length === 0 ||
      filter.statuses.includes(status);
    const matchesTags = filter.tags.length === 0 ||
      filter.tags.every((tag) => tags.includes(tag));
    const requestedProjects = filter.projectIds ?? [];
    const requestedLocations = filter.locations ?? [];
    const requestedReleases = filter.releases ?? [];
    const requestedCapabilities = filter.capabilities ?? [];
    const matchesProjects = requestedProjects.length === 0 ||
      requestedProjects.some((projectId) => projectIds.includes(projectId));
    const matchesLocations = requestedLocations.length === 0 ||
      requestedLocations.some((location) =>
        locations.some((candidate) =>
          candidate.toLocaleLowerCase().includes(location.toLocaleLowerCase())
        )
      );
    const matchesReleases = requestedReleases.length === 0 ||
      requestedReleases.includes(item.release);
    const matchesCapabilities = requestedCapabilities.length === 0 ||
      requestedCapabilities.every((capability) =>
        capabilities.includes(capability)
      );
    const matchesAge = filter.maxContactAgeMinutes === undefined ||
      filter.maxContactAgeMinutes === null ||
      ageMinutes <= filter.maxContactAgeMinutes;
    return matchesQuery && matchesStatus && matchesTags && matchesProjects &&
      matchesLocations && matchesReleases && matchesCapabilities && matchesAge;
  });
}

/** Sort a complete filtered fleet before pagination using a deterministic node-id tie breaker. */
export function sortFleet(
  nodes: readonly FleetNode[],
  sort: "name" | "status" | "last-seen",
  now: Date,
): readonly FleetNode[] {
  return [...nodes].sort((left, right) => {
    const primary = sort === "last-seen"
      ? Date.parse(right.lastSeenAt) - Date.parse(left.lastSeenAt)
      : sort === "status"
      ? classifyNode(left, now).localeCompare(classifyNode(right, now))
      : left.name.localeCompare(right.name);
    return primary || left.id.localeCompare(right.id);
  });
}

/** Resolve active maintenance, preferring a node window over a project window. */
export function resolveMaintenance(
  windows: readonly MaintenanceWindow[],
  nodeId: string,
  projectIds: readonly string[],
  now: Date,
): MaintenanceWindow | null {
  const time = now.getTime();
  const active = windows.filter((window) =>
    window.state === "scheduled" && Date.parse(window.startsAt) <= time &&
    Date.parse(window.endsAt) > time
  );
  return active.find((window) =>
    window.scope.type === "nodes" && window.scope.nodeIds.includes(nodeId)
  ) ??
    active.find((window) =>
      window.scope.type === "node" && window.scope.nodeId === nodeId
    ) ??
    active.find((window) =>
      window.scope.type === "project" &&
      projectIds.includes(window.scope.projectId)
    ) ??
    active.find((window) => window.scope.type === "fleet") ??
    null;
}

/** Result of evaluating one numeric alert rule. */
export type AlertEvaluation =
  | { readonly state: "normal" }
  | { readonly state: "firing"; readonly severity: "warning" | "critical" }
  | { readonly state: "suppressed"; readonly severity: "warning" | "critical" }
  | { readonly state: "unavailable" };

/** Evaluate a rule against sanitized numeric metrics and maintenance state. */
export function evaluateAlertRule(
  rule: AlertRule,
  metrics: Readonly<Partial<Record<AlertRule["metric"], number>>>,
  maintenance: boolean,
  previouslyMatched = false,
): AlertEvaluation {
  if (!rule.enabled) return { state: "normal" };
  const value = metrics[rule.metric];
  if (value === undefined || !Number.isFinite(value)) {
    return { state: "unavailable" };
  }
  const threshold =
    previouslyMatched && (rule.comparison === "gt" || rule.comparison === "gte")
      ? rule.threshold - rule.hysteresis
      : previouslyMatched &&
          (rule.comparison === "lt" || rule.comparison === "lte")
      ? rule.threshold + rule.hysteresis
      : rule.threshold;
  const matched = rule.comparison === "gt"
    ? (previouslyMatched ? value >= threshold : value > threshold)
    : rule.comparison === "gte"
    ? value >= threshold
    : rule.comparison === "lt"
    ? (previouslyMatched ? value <= threshold : value < threshold)
    : rule.comparison === "lte"
    ? value <= threshold
    : Math.abs(value - threshold) <= (previouslyMatched ? rule.hysteresis : 0);
  if (!matched) return { state: "normal" };
  return maintenance
    ? { state: "suppressed", severity: rule.severity }
    : { state: "firing", severity: rule.severity };
}

/** Persisted state for duration, cooldown, suppression, and recovery evaluation. */
export interface AlertStateSnapshot {
  readonly state: "normal" | "pending" | "firing" | "suppressed";
  readonly firstMatchedAt: string | null;
  readonly lastNotifiedAt: string | null;
}

/** Deterministic rule-state transition with duration and notification cooldown. */
export function transitionAlertState(
  current: AlertStateSnapshot | null,
  evaluation: AlertEvaluation,
  now: Date,
  durationMinutes: number,
  cooldownMinutes: number,
): {
  readonly next: AlertStateSnapshot;
  readonly notification: "firing" | "recovery" | null;
} {
  const nowIso = now.toISOString();
  if (evaluation.state === "unavailable") {
    return {
      next: current ??
        { state: "normal", firstMatchedAt: null, lastNotifiedAt: null },
      notification: null,
    };
  }
  if (evaluation.state === "normal") {
    const notification = current?.state === "firing" ? "recovery" : null;
    return {
      next: {
        state: "normal",
        firstMatchedAt: null,
        lastNotifiedAt: notification ? nowIso : current?.lastNotifiedAt ?? null,
      },
      notification,
    };
  }
  const firstMatchedAt =
    current?.state === "pending" || current?.state === "firing" ||
      current?.state === "suppressed"
      ? current.firstMatchedAt ?? nowIso
      : nowIso;
  const mature =
    now.getTime() - Date.parse(firstMatchedAt) >= durationMinutes * 60_000;
  if (!mature) {
    return {
      next: {
        state: "pending",
        firstMatchedAt,
        lastNotifiedAt: current?.lastNotifiedAt ?? null,
      },
      notification: null,
    };
  }
  if (evaluation.state === "suppressed") {
    return {
      next: {
        state: "suppressed",
        firstMatchedAt,
        lastNotifiedAt: current?.lastNotifiedAt ?? null,
      },
      notification: null,
    };
  }
  const cooldownElapsed = !current?.lastNotifiedAt ||
    now.getTime() - Date.parse(current.lastNotifiedAt) >=
      cooldownMinutes * 60_000;
  // A continuous episode emits once; cooldown only gates a new episode after recovery/flapping.
  const notification = current?.state !== "firing" && cooldownElapsed
    ? "firing"
    : null;
  return {
    next: {
      state: "firing",
      firstMatchedAt,
      lastNotifiedAt: notification ? nowIso : current?.lastNotifiedAt ?? null,
    },
    notification,
  };
}

/** Produce a deterministic 15-minute incident-correlation bucket. */
export function correlateIncidentKey(
  projectId: string,
  family: string,
  occurredAt: string,
): string {
  const timestamp = Date.parse(occurredAt);
  if (!Number.isFinite(timestamp)) throw new Error("invalid-incident-time");
  return `${projectId}:${family}:${
    new Date(Math.floor(timestamp / 900_000) * 900_000).toISOString()
  }`;
}

/** Derive a stable dependency-component family for unhealthy reported services. */
export function serviceIncidentFamily(
  services: readonly ServiceObservation[],
): string {
  const unhealthy = services.filter((service) =>
    service.health === "unhealthy" || service.state === "stopped" ||
    service.state === "restarting"
  );
  if (unhealthy.length === 0) return "service:unknown";
  const related = new Set(unhealthy.map((service) => service.catalogId));
  let changed = true;
  while (changed) {
    changed = false;
    for (const service of services) {
      const connected = related.has(service.catalogId) ||
        service.dependencies.some((dependency) => related.has(dependency));
      if (!connected) continue;
      if (!related.has(service.catalogId)) {
        related.add(service.catalogId);
        changed = true;
      }
      for (const dependency of service.dependencies) {
        if (!related.has(dependency)) {
          related.add(dependency);
          changed = true;
        }
      }
    }
  }
  return `service:${[...related].sort().join("+")}`;
}

/** Normalize alert metrics into bounded incident families, including service dependency context. */
export function alertIncidentFamily(
  metric: AlertRule["metric"],
  services: readonly ServiceObservation[],
): string {
  if (metric === "container-health" || metric === "restart-count") {
    return serviceIncidentFamily(services);
  }
  if (metric === "contact-age") return "availability";
  if (metric === "cpu" || metric === "memory" || metric === "disk") {
    return "resource-pressure";
  }
  if (metric === "backup-age") return "backup";
  return "version-compliance";
}

/** Classify reported software against an operator-approved target. */
export function classifyCompliance(
  reported: string | undefined,
  desired: string,
  immutable: boolean,
  minimumSupported?: string | null,
):
  | "compliant"
  | "update-available"
  | "unsupported"
  | "drifted"
  | "unverifiable"
  | "unknown" {
  if (!reported) return "unknown";
  if (!immutable || reported === "latest" || reported === "release") {
    return "unverifiable";
  }
  if (reported === desired) return "compliant";
  const semantic = (value: string): readonly number[] | null => {
    const match = /^(\d+)\.(\d+)\.(\d+)/u.exec(value);
    return match
      ? [Number(match[1]), Number(match[2]), Number(match[3])]
      : null;
  };
  const reportedParts = semantic(reported);
  const desiredParts = semantic(desired);
  const minimumParts = minimumSupported ? semantic(minimumSupported) : null;
  const compare = (left: readonly number[], right: readonly number[]): number =>
    left[0] !== right[0]
      ? (left[0] ?? 0) - (right[0] ?? 0)
      : left[1] !== right[1]
      ? (left[1] ?? 0) - (right[1] ?? 0)
      : (left[2] ?? 0) - (right[2] ?? 0);
  if (
    reportedParts && minimumParts && compare(reportedParts, minimumParts) < 0
  ) return "unsupported";
  return reportedParts && desiredParts ? "update-available" : "drifted";
}

/** Classify a service image using immutable local registry-digest evidence. */
export function classifyServiceCompliance(
  reportedDigest: string | null,
  desiredDigest: string | undefined,
): "compliant" | "update-available" | "unverifiable" | "unknown" {
  if (!desiredDigest) return "unknown";
  if (!reportedDigest) return "unverifiable";
  return reportedDigest === desiredDigest ? "compliant" : "update-available";
}

function csvCell(value: string | number | null): string {
  const raw = value === null ? "" : String(value);
  const safe = /^[=+\-@]/u.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/u.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/** Build a bounded, spreadsheet-injection-safe CSV document. */
export function buildCsv(
  headers: readonly string[],
  rows: readonly (readonly (string | number | null)[])[],
): string {
  if (
    headers.length === 0 || headers.length > 32 || rows.length > 10_000 ||
    rows.some((row) => row.length !== headers.length)
  ) throw new Error("invalid-csv-shape");
  const output = [
    headers.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\r\n") + "\r\n";
  if (new TextEncoder().encode(output).byteLength > 2_000_000) {
    throw new Error("csv-too-large");
  }
  return output;
}

/** Build a compact, valid multi-page PDF containing bounded plain-text evidence. */
export function buildSimplePdf(
  title: string,
  lines: readonly string[],
): Uint8Array {
  if (
    title.length > 120 || lines.length > 5_000 ||
    lines.reduce((total, line) => total + line.length, 0) > 500_000
  ) throw new Error("pdf-content-too-large");
  const safe = (value: string): string =>
    value.normalize("NFKD").replaceAll(/[^\x20-\x7E]/gu, "?").replaceAll(
      "\\",
      "\\\\",
    ).replaceAll("(", "\\(").replaceAll(")", "\\)");
  const wrapped = lines.flatMap((line) => line.match(/.{1,150}/gu) ?? [""]);
  if (wrapped.length > 5_000) throw new Error("pdf-content-too-large");
  const pageLines = Array.from({
    length: Math.max(1, Math.ceil(wrapped.length / 38)),
  }, (_, index) => wrapped.slice(index * 38, (index + 1) * 38));
  const fontObject = 3 + pageLines.length * 2;
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    `<< /Type /Pages /Kids [${
      pageLines.map((_, index) => `${3 + index * 2} 0 R`).join(" ")
    }] /Count ${pageLines.length} >>`,
  ];
  pageLines.forEach((page, index) => {
    const pageNumber = index + 1;
    const commands = [
      "BT",
      "/F1 16 Tf",
      "50 780 Td",
      `(${safe(title)}) Tj`,
      "/F1 10 Tf",
      "0 -18 Td",
      `(Page ${pageNumber} of ${pageLines.length}) Tj`,
      ...page.flatMap((line) => ["0 -18 Td", `(${safe(line)}) Tj`]),
      "ET",
    ].join("\n");
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontObject} 0 R >> >> /Contents ${
        4 + index * 2
      } 0 R >>`,
    );
    objects.push(
      `<< /Length ${
        new TextEncoder().encode(commands).byteLength
      } >>\nstream\n${commands}\nendstream`,
    );
  });
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  let document = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(new TextEncoder().encode(document).byteLength);
    document += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xref = new TextEncoder().encode(document).byteLength;
  document += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n${
    offsets.slice(1).map((offset) =>
      `${String(offset).padStart(10, "0")} 00000 n `
    ).join("\n")
  }\ntrailer\n<< /Size ${
    objects.length + 1
  } /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new TextEncoder().encode(document);
}

/** Classify backup recency independently from restore-test evidence. */
export function classifyBackupPosture(
  backup: {
    readonly capturedAt: string;
    readonly restoreTestedAt: string | null;
    readonly restoreSchemaVerified?: boolean;
    readonly restoreDecryptionVerified?: boolean;
    readonly keyEscrowVerified?: boolean;
    readonly state: "succeeded" | "failed";
  },
  now: Date,
):
  | "verified"
  | "recent-unverified"
  | "failed"
  | "overdue"
  | "restore-overdue"
  | "restore-failed"
  | "escrow-missing" {
  if (backup.state === "failed") return "failed";
  const age = now.getTime() - Date.parse(backup.capturedAt);
  if (!Number.isFinite(age) || age > 30 * 86_400_000) return "overdue";
  if (!backup.keyEscrowVerified) return "escrow-missing";
  if (!backup.restoreTestedAt) return "recent-unverified";
  if (!backup.restoreSchemaVerified || !backup.restoreDecryptionVerified) {
    return "restore-failed";
  }
  return now.getTime() - Date.parse(backup.restoreTestedAt) > 90 * 86_400_000
    ? "restore-overdue"
    : "verified";
}

/** Apply project coordinate privacy before data reaches a browser or tile provider. */
export function mapCoordinates(
  policy: ProjectMapPolicy,
  latitude: number | null,
  longitude: number | null,
): { readonly latitude: number; readonly longitude: number } | null {
  if (policy === "hidden" || latitude === null || longitude === null) {
    return null;
  }
  if (policy === "approximate") {
    return {
      latitude: Math.round(latitude * 100) / 100,
      longitude: Math.round(longitude * 100) / 100,
    };
  }
  return { latitude, longitude };
}

/** Deterministically group nearby policy-filtered project points for the local overview map. */
export function clusterProjectMapPoints(
  projects: readonly Project[],
  cellDegrees = 5,
): readonly {
  readonly id: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly names: readonly string[];
}[] {
  const cells = new Map<
    string,
    { latitudeTotal: number; longitudeTotal: number; names: string[] }
  >();
  for (const project of projects) {
    if (project.latitude === null || project.longitude === null) continue;
    const key = `${Math.floor((project.latitude + 90) / cellDegrees)}:${
      Math.floor((project.longitude + 180) / cellDegrees)
    }`;
    const cell = cells.get(key) ??
      { latitudeTotal: 0, longitudeTotal: 0, names: [] };
    cell.latitudeTotal += project.latitude;
    cell.longitudeTotal += project.longitude;
    cell.names.push(project.name);
    cells.set(key, cell);
  }
  return [...cells.entries()].map(([id, cell]) => ({
    id,
    latitude: cell.latitudeTotal / cell.names.length,
    longitude: cell.longitudeTotal / cell.names.length,
    names: [...cell.names].sort(),
  })).sort((left, right) => left.id.localeCompare(right.id));
}

/** Topologically order reported services for dependency-aware rendering. */
export function sortServiceTopology(
  services: readonly ServiceObservation[],
): readonly ServiceObservation[] {
  const byId = new Map(services.map((service) => [service.catalogId, service]));
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const ordered: ServiceObservation[] = [];
  const visit = (id: string): void => {
    if (visiting.has(id)) throw new Error("service-dependency-cycle");
    if (visited.has(id)) return;
    const service = byId.get(id);
    if (!service) return;
    visiting.add(id);
    for (const dependency of service.dependencies) visit(dependency);
    visiting.delete(id);
    visited.add(id);
    ordered.push(service);
  };
  for (const service of services) visit(service.catalogId);
  return ordered;
}

/** Analyze dependency edges without hiding unknown targets or crashing on cycles. */
export function analyzeServiceTopology(
  services: readonly ServiceObservation[],
): {
  readonly state: "ordered";
  readonly services: readonly ServiceObservation[];
  readonly unknownDependencies: readonly string[];
} | {
  readonly state: "cycle";
  readonly services: readonly ServiceObservation[];
  readonly unknownDependencies: readonly string[];
} {
  const known = new Set(services.map((service) => service.catalogId));
  const unknownDependencies = [
    ...new Set(
      services.flatMap((service) => service.dependencies).filter((dependency) =>
        !known.has(dependency)
      ),
    ),
  ].sort();
  try {
    return {
      state: "ordered",
      services: sortServiceTopology(services),
      unknownDependencies,
    };
  } catch (error: unknown) {
    if (
      !(error instanceof Error) || error.message !== "service-dependency-cycle"
    ) throw error;
    return {
      state: "cycle",
      services: [...services].sort((left, right) =>
        left.catalogId.localeCompare(right.catalogId)
      ),
      unknownDependencies,
    };
  }
}
